import React, { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { getCardColor2 } from "../../../helpers/helpers";

const proposalHours = (request) => {
  const shiftHours = Math.max(
    0,
    (new Date(request.proposed_clocked_out_at) -
      new Date(request.proposed_clocked_in_at)) /
      3600000
  );
  const unpaidBreakHours = (request.proposed_breaks || [])
    .filter((item) => !item.is_paid)
    .reduce(
      (total, item) =>
        total + (item.clocked_out_at - item.clocked_in_at) / 3600000,
      0
    );
  return {
    shiftHours,
    paidHours: Math.max(0, shiftHours - unpaidBreakHours),
  };
};

function EmployeeRequestCard({ request, onEdit, onDelete, rolesList }) {
  const clockIn = new Date(request.proposed_clocked_in_at);
  const clockOut = new Date(request.proposed_clocked_out_at);
  const hours = proposalHours(request);
  const role = rolesList.find((item) => item.id === request.role?.id);
  const roleName = role?.role_name || request.role?.role_name;
  const roleType = role?.role_type;
  const roleColor = getCardColor2(request.role?.id, rolesList, false);
  const employeeCanManage = request.status === "pending" && Boolean(onEdit);
  const breakSummary = request.proposed_breaks
    ?.map((item) => {
      const minutes = Math.round(
        (item.clocked_out_at - item.clocked_in_at) / 60000
      );
      return `${minutes}m ${item.is_paid ? "paid" : "unpaid"}`;
    })
    .join(", ");
  const status =
    request.status === "approved"
      ? { label: "Approved", classes: "border-emerald-200 bg-emerald-50 text-emerald-700" }
      : request.status === "denied"
      ? { label: "Denied", classes: "border-rose-200 bg-rose-50 text-rose-700" }
      : { label: "Pending", classes: "border-amber-200 bg-amber-50 text-amber-700" };

  return (
    <div
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${
        employeeCanManage
          ? "cursor-pointer hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-200"
          : ""
      }`}
      onClick={() => employeeCanManage && onEdit(request)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (employeeCanManage && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onEdit(request);
        }
      }}
      role={employeeCanManage ? "button" : undefined}
      tabIndex={employeeCanManage ? 0 : undefined}
    >
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs font-semibold text-slate-500">
          {request.request_type === "edit" ? "Time entry edit" : "Missing time entry"}
        </p>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${status.classes}`}>
          {status.label}
        </span>
      </div>

      <p className="mt-2 text-lg font-bold text-slate-900">
        {clockIn.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })} – {clockOut.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </p>
      <p className="mt-0.5 text-sm text-slate-500">
        {clockIn.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}
      </p>

      <div className="mt-3 flex items-center">
        <span className={`mr-2 h-3 w-3 rounded-full ${roleColor}`} />
        <span className="text-sm font-semibold text-slate-800">
          {roleName || "No role selected"}
        </span>
        {roleType && <span className="text-sm text-slate-500"> · {roleType}</span>}
      </div>
      <p className="mt-2 text-sm text-slate-600">
        {hours.shiftHours.toFixed(2)}h shift · {hours.paidHours.toFixed(2)}h paid
      </p>
      {breakSummary && (
        <p className="mt-1 text-sm text-slate-500">Breaks: {breakSummary}</p>
      )}

      <div className="mt-4 border-t border-slate-200 pt-3">
        <p className="text-xs font-semibold text-slate-500">Note</p>
        <p className="mt-1 text-sm leading-5 text-slate-700">
          {request.employee_note}
        </p>
      </div>
      {request.manager_note && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          <p className="text-xs font-semibold text-slate-500">Manager response</p>
          <p className="mt-1 text-sm leading-5 text-slate-700">
            {request.manager_note}
          </p>
        </div>
      )}

      {employeeCanManage && (
        <div className="mt-4 flex gap-3 border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onDelete?.(request);
            }}
            className="flex min-h-[42px] flex-1 items-center justify-center rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-100"
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            <span className="ml-2">Delete</span>
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onEdit(request);
            }}
            className="flex min-h-[42px] flex-1 items-center justify-center rounded-full bg-violet-700 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
            <span className="ml-2">Edit request</span>
          </button>
        </div>
      )}
    </div>
  );
}

function ManagerRequestCard({
  request,
  note,
  onNoteChange,
  onReview,
  saving,
  readOnly,
  showEmployeeName,
  rolesList,
}) {
  const clockIn = new Date(request.proposed_clocked_in_at);
  const clockOut = new Date(request.proposed_clocked_out_at);
  const hours = proposalHours(request);
  const role = rolesList.find((item) => item.id === request.role?.id);
  const roleName = role?.role_name || request.role?.role_name;
  const roleType = role?.role_type;
  const roleColor = getCardColor2(request.role?.id, rolesList, false);
  const breakSummary = request.proposed_breaks
    ?.map((item) => {
      const minutes = Math.round(
        (item.clocked_out_at - item.clocked_in_at) / 60000
      );
      return `${minutes}m ${item.is_paid ? "paid" : "unpaid"}`;
    })
    .join(", ");
  const status =
    request.status === "approved"
      ? { label: "Approved", classes: "border-emerald-200 bg-emerald-50 text-emerald-700" }
      : request.status === "denied"
      ? { label: "Denied", classes: "border-rose-200 bg-rose-50 text-rose-700" }
      : { label: "Pending", classes: "border-amber-200 bg-amber-50 text-amber-700" };
  const canReview = !readOnly && request.status === "pending";

  return (
    <article className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold text-slate-500">
            {request.request_type === "edit" ? "Time entry edit" : "Missing time entry"}
          </p>
          {showEmployeeName && request.store_team_member?.full_name && (
            <p className="mt-1 text-sm font-semibold text-violet-700">
              {request.store_team_member.full_name}
            </p>
          )}
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${status.classes}`}>
          {status.label}
        </span>
      </div>

      <p className="mt-3 text-lg font-bold text-slate-900">
        {clockIn.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })} – {clockOut.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </p>
      <p className="mt-0.5 text-sm text-slate-500">
        {clockIn.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}
      </p>

      <div className="mt-3 flex items-center">
        <span className={`mr-2 h-3 w-3 rounded-full ${roleColor}`} />
        <span className="text-sm font-semibold text-slate-800">
          {roleName || "No role selected"}
        </span>
        {roleType && <span className="text-sm text-slate-500"> · {roleType}</span>}
      </div>
      <p className="mt-2 text-sm text-slate-600">
        {hours.shiftHours.toFixed(2)}h shift · {hours.paidHours.toFixed(2)}h paid
      </p>
      <div className="mt-1 min-h-[20px]">
        {breakSummary && (
          <p className="text-sm text-slate-500">Breaks: {breakSummary}</p>
        )}
      </div>

      <div className="mt-4 border-t border-slate-200 pb-4 pt-3">
        <p className="text-xs font-semibold text-slate-500">Employee note</p>
        <p className="mt-1 text-sm leading-5 text-slate-700">
          {request.employee_note}
        </p>
      </div>
      {request.manager_note && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          <p className="text-xs font-semibold text-slate-500">Manager response</p>
          <p className="mt-1 text-sm leading-5 text-slate-700">
            {request.manager_note}
          </p>
        </div>
      )}

      {canReview && (
        <div className="mt-3 border-t border-slate-200 pt-4">
          <label
            htmlFor={`manager-note-${request.uuid}`}
            className="text-xs font-semibold text-slate-500"
          >
            Response note
          </label>
          <input
            id={`manager-note-${request.uuid}`}
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            placeholder="Optional response note"
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition-colors focus:border-violet-600 focus:ring-2 focus:ring-violet-100"
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => onReview("denied")}
              className="rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Deny
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => onReview("approved")}
              className="rounded-full bg-violet-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Approve and apply
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

function TimecardChangeRequestsPanel({
  requests,
  onReview,
  onEdit,
  onDelete,
  readOnly = false,
  showEmployeeName = false,
  employeeView = false,
  rolesList = [],
  showHeading = true,
}) {
  const [notes, setNotes] = useState({});
  const [savingUuid, setSavingUuid] = useState(null);
  if (!requests?.length) return null;

  const review = async (request, decision) => {
    setSavingUuid(request.uuid);
    try {
      await onReview(request.uuid, decision, notes[request.uuid] || "");
    } finally {
      setSavingUuid(null);
    }
  };

  if (employeeView) {
    return (
      <section>
        <h3 className="font-semibold text-slate-900">Time entry requests</h3>
        <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-1">
          {requests.map((request) => (
            <EmployeeRequestCard
              key={request.uuid}
              request={request}
              onEdit={onEdit}
              onDelete={onDelete}
              rolesList={rolesList}
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section>
      {showHeading && (
        <h3 className="font-semibold text-slate-900">
          {readOnly ? "Time entry requests" : "Time entry change requests"}
        </h3>
      )}
      <div
        className={`${showHeading ? "mt-3" : ""} grid items-start gap-4 md:grid-cols-1 ${
          requests.length === 1
            ? "mx-auto w-full max-w-2xl grid-cols-1"
            : "grid-cols-2"
        }`}
      >
        {requests.map((request) => (
          <ManagerRequestCard
            key={request.uuid}
            request={request}
            note={notes[request.uuid] || ""}
            onNoteChange={(value) =>
              setNotes((previous) => ({
                ...previous,
                [request.uuid]: value,
              }))
            }
            onReview={(decision) => review(request, decision)}
            saving={savingUuid === request.uuid}
            readOnly={readOnly}
            showEmployeeName={showEmployeeName}
            rolesList={rolesList}
          />
        ))}
      </div>
    </section>
  );
}

export default TimecardChangeRequestsPanel;
